import { DataRetentionAuditLog, AuditActionType, Jurisdiction } from '../types/dataRetention';

// Helper to generate random dates in the past 6 months
const getRandomDate = (daysAgo: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - Math.floor(Math.random() * daysAgo));
  date.setHours(Math.floor(Math.random() * 24));
  date.setMinutes(Math.floor(Math.random() * 60));
  date.setSeconds(Math.floor(Math.random() * 60));
  return date;
};

// Sample user names
const userNames = [
  'John Anderson', 'Sarah Mitchell', 'Michael Chen', 'Emma Williams', 'David Brown',
  'Lisa Garcia', 'James Rodriguez', 'Maria Johnson', 'Robert Martinez', 'Jennifer Davis',
  'William Taylor', 'Jessica Anderson', 'Thomas White', 'Ashley Thomas', 'Christopher Jackson',
  'Amanda Harris', 'Daniel Martin', 'Melissa Thompson', 'Matthew Garcia', 'Laura Robinson'
];

// Sample admin names
const adminNames = [
  'Admin Smith', 'Compliance Officer Jones', 'System Admin Davis', 'Data Manager Wilson',
  'Security Lead Brown', 'Privacy Officer Garcia', 'Admin Taylor', 'Compliance Manager Lee'
];

// Sample IP addresses
const ipAddresses = [
  '192.168.1.100', '192.168.1.101', '192.168.1.102', '10.0.0.50', '10.0.0.51',
  '172.16.0.100', '172.16.0.101', '192.168.2.100', '10.1.1.50'
];

// Sample user agents
const userAgents = [
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64; rv:122.0) Gecko/20100101 Firefox/122.0',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.2 Safari/605.1.15',
  'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/121.0.0.0 Safari/537.36'
];

// Sample jurisdictions
const jurisdictions: (Jurisdiction | 'SYSTEM')[] = ['US', 'CA', 'QC', 'UK', 'SYSTEM'];

// Helper to get random element from array
const randomElement = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

// Generate audit log entries
export const mockAuditLogs: DataRetentionAuditLog[] = [];

let idCounter = 1;

// Status changes (20 entries)
for (let i = 0; i < 20; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;
  const statuses = ['active', 'inactive', 'scheduled', 'purged'];
  const beforeStatus = randomElement(statuses);
  const afterStatus = randomElement(statuses.filter(s => s !== beforeStatus));

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${1000 + i}`,
    userName,
    actionType: 'status_change',
    performedBy: Math.random() > 0.3 ? `admin-${100 + Math.floor(Math.random() * 8)}` : null,
    performedByName: Math.random() > 0.3 ? randomElement(adminNames) : 'SYSTEM',
    performedAt: getRandomDate(180),
    jurisdiction,
    details: `Status changed from ${beforeStatus} to ${afterStatus}`,
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      beforeValue: beforeStatus,
      afterValue: afterStatus,
      reason: Math.random() > 0.5 ? 'User request' : 'Automated policy enforcement',
      notes: Math.random() > 0.5 ? 'Status updated per compliance requirements' : ''
    }
  });
}

// Manual interventions (15 entries)
for (let i = 0; i < 15; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;
  const interventionTypes = [
    'Manual purge override',
    'Data correction',
    'Account restoration',
    'Compliance review',
    'Legal hold investigation'
  ];

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${2000 + i}`,
    userName,
    actionType: 'manual_intervention',
    performedBy: `admin-${100 + Math.floor(Math.random() * 8)}`,
    performedByName: randomElement(adminNames),
    performedAt: getRandomDate(180),
    jurisdiction,
    details: randomElement(interventionTypes),
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      reason: 'Manual administrative action required',
      notes: 'Intervention performed after compliance review'
    }
  });
}

// Data exports (20 entries)
for (let i = 0; i < 20; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${3000 + i}`,
    userName,
    actionType: 'data_export',
    performedBy: `admin-${100 + Math.floor(Math.random() * 8)}`,
    performedByName: randomElement(adminNames),
    performedAt: getRandomDate(180),
    jurisdiction,
    details: 'User data exported for compliance request',
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      reason: 'GDPR/CCPA data portability request',
      exportFormat: randomElement(['JSON', 'CSV', 'PDF']),
      exportSize: `${(Math.random() * 50 + 1).toFixed(2)} MB`
    },
    exportFilePath: `/exports/${jurisdiction.toLowerCase()}/user-${3000 + i}-${Date.now()}.zip`
  });
}

// Deletion executions (12 entries)
for (let i = 0; i < 12; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${4000 + i}`,
    userName,
    actionType: 'deletion_executed',
    performedBy: null,
    performedByName: 'SYSTEM',
    performedAt: getRandomDate(180),
    jurisdiction,
    details: 'User data permanently deleted per retention policy',
    metadata: {
      ipAddress: '10.0.0.1',
      userAgent: 'Data Retention Service v2.1',
      beforeValue: { status: 'scheduled', dataSize: `${(Math.random() * 10 + 1).toFixed(2)} MB` },
      afterValue: { status: 'purged', dataSize: '0 MB' },
      reason: 'Retention period expired',
      relatedRequestId: `req-${5000 + i}`
    }
  });
}

// Extension granted (10 entries)
for (let i = 0; i < 10; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;
  const extensionDays = randomElement([30, 60, 90, 180, 365]);

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${5000 + i}`,
    userName,
    actionType: 'extension_granted',
    performedBy: `admin-${100 + Math.floor(Math.random() * 8)}`,
    performedByName: randomElement(adminNames),
    performedAt: getRandomDate(180),
    jurisdiction,
    details: `Retention period extended by ${extensionDays} days`,
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      beforeValue: { retentionDays: 730 },
      afterValue: { retentionDays: 730 + extensionDays },
      reason: randomElement(['Active litigation', 'Ongoing investigation', 'Business requirement', 'Customer request']),
      notes: `Extension approved by compliance team`
    }
  });
}

// Legal hold applied (8 entries)
for (let i = 0; i < 8; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${6000 + i}`,
    userName,
    actionType: 'legal_hold_applied',
    performedBy: `admin-${100 + Math.floor(Math.random() * 8)}`,
    performedByName: randomElement(adminNames),
    performedAt: getRandomDate(180),
    jurisdiction,
    details: 'Legal hold placed on user account',
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      beforeValue: { legalHold: false },
      afterValue: { legalHold: true },
      reason: randomElement(['Litigation pending', 'Regulatory investigation', 'Legal discovery request']),
      notes: 'Legal hold applied per legal team directive',
      caseNumber: `CASE-2025-${1000 + i}`
    }
  });
}

// Legal hold removed (6 entries)
for (let i = 0; i < 6; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${6000 + i}`,
    userName,
    actionType: 'legal_hold_removed',
    performedBy: `admin-${100 + Math.floor(Math.random() * 8)}`,
    performedByName: randomElement(adminNames),
    performedAt: getRandomDate(90),
    jurisdiction,
    details: 'Legal hold removed from user account',
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      beforeValue: { legalHold: true },
      afterValue: { legalHold: false },
      reason: 'Case resolved',
      notes: 'Legal hold lifted per legal team approval',
      caseNumber: `CASE-2025-${1000 + i}`
    }
  });
}

// Policy changes (5 entries)
for (let i = 0; i < 5; i++) {
  const policyChanges = [
    { name: 'CCPA retention period', before: '1095 days', after: '1095 days', jurisdiction: 'US' as Jurisdiction },
    { name: 'PIPEDA retention period', before: '730 days', after: '730 days', jurisdiction: 'CA' as Jurisdiction },
    { name: 'Quebec Law 25 compliance', before: '730 days', after: '730 days', jurisdiction: 'QC' as Jurisdiction },
    { name: 'UK GDPR retention period', before: '730 days', after: '730 days', jurisdiction: 'UK' as Jurisdiction },
    { name: 'Global auto-purge setting', before: 'Enabled', after: 'Enabled', jurisdiction: 'SYSTEM' as 'SYSTEM' }
  ];

  const policy = policyChanges[i];

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: null,
    userName: undefined,
    actionType: 'policy_change',
    performedBy: `admin-${100 + Math.floor(Math.random() * 8)}`,
    performedByName: randomElement(adminNames),
    performedAt: getRandomDate(180),
    jurisdiction: policy.jurisdiction,
    details: `Policy updated: ${policy.name}`,
    metadata: {
      ipAddress: randomElement(ipAddresses),
      userAgent: randomElement(userAgents),
      beforeValue: { [policy.name]: policy.before },
      afterValue: { [policy.name]: policy.after },
      reason: 'Compliance policy update',
      notes: 'Policy change approved by compliance committee'
    }
  });
}

// Warning sent (10 entries)
for (let i = 0; i < 10; i++) {
  const userName = randomElement(userNames);
  const jurisdiction = randomElement(['US', 'CA', 'QC', 'UK']) as Jurisdiction;
  const warningLevel = randomElement([90, 30, 7]);

  mockAuditLogs.push({
    id: `log-${idCounter++}`,
    userId: `user-${7000 + i}`,
    userName,
    actionType: 'warning_sent',
    performedBy: null,
    performedByName: 'SYSTEM',
    performedAt: getRandomDate(180),
    jurisdiction,
    details: `${warningLevel}-day purge warning sent`,
    metadata: {
      ipAddress: '10.0.0.1',
      userAgent: 'Notification Service v1.5',
      warningLevel,
      daysUntilPurge: warningLevel,
      notificationMethod: randomElement(['Email', 'Email + SMS']),
      emailSent: true
    }
  });
}

// Sort by date (newest first)
mockAuditLogs.sort((a, b) => b.performedAt.getTime() - a.performedAt.getTime());

export default mockAuditLogs;
